import React from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import KeenTracking from 'keen-tracking'
import isMobileBrowser from 'is-mobile'

import * as eventTrackerActions from 'agency/redux/modules/analytics/eventTracker'

const currentTime = () => new Date().getTime()

const getWindowHeight = () => window.innerHeight || document.documentElement.clientHeight

const getScrollOffset = () => (window.pageYOffset !== undefined
  ? window.pageYOffset
  : (document.documentElement || document.body.parentNode || document.body).scrollTop)

const getScrollableArea = () => {
  const { body } = document
  const html = document.documentElement

  return Math.max(body.scrollHeight, body.offsetHeight, html.clientHeight, html.scrollHeight, html.offsetHeight) || null
}

const registerUnloadListener = (unloadHandler, visibilityHandler) => {
  const isMobile = isMobileBrowser()

  if (isMobile){
    document.addEventListener('visibilitychange', visibilityHandler)
  } else {
    const attachEvent = window.attachEvent || window.addEventListener
    const whichEvent = window.attachEvent ? 'onbeforeunload' : 'beforeunload'

    attachEvent(whichEvent, unloadHandler)
  }
}

class AnalyticsTracker extends React.Component {
  state = {
    unloadProcessed: false,
    maxY: 0,
    maxRatio: 0,
    startTime: currentTime(),
  }

  componentDidMount(){
    const {
      clientConfig, collectionKey, initializeClient, trackingMeta, trackScrollPosition,
    } = this.props

    // Setup Tracking Client
    const analyticsClient = initializeClient(clientConfig)
    analyticsClient.recordEvent(collectionKey, {
      title: window.document.title,
      views: 1,
      ...trackingMeta,
    })

    // Setup Scroll Handler
    if (trackScrollPosition) this._setupScrollHandler()

    // Register Unload
    registerUnloadListener(this._handleUnloadEvent, this._handleVisibilityChangeEvent)
  }

  _resetState = () => {
    this.setState({
      maxY: 0,
      maxRatio: 0,
      startTime: currentTime(),
    })
  }

  _setupScrollHandler = () => {
    const windowListener = KeenTracking.utils.listener('window')
    windowListener.on('scroll', this._sampleScrollState)
  }

  _sampleScrollState = () => {
    const { maxY: stateMaxY } = this.state

    const yPosition = getScrollOffset() + getWindowHeight()
    const maxY = yPosition > stateMaxY ? yPosition : stateMaxY

    const scrollableArea = getScrollableArea()

    this.setState({
      maxY,
      maxRatio: parseFloat(Number(maxY / scrollableArea).toFixed(2)),
    })
  }

  _getAnalyticsData = () => {
    const { trackScrollPosition } = this.props
    const { maxRatio, startTime } = this.state

    const data = {
      secondsOnPage: Number(currentTime() - startTime) / 1000,
    }

    if (trackScrollPosition){
      data.readRatio = maxRatio
    }

    return data
  }

  _handleVisibilityChangeEvent = () => {
    if (document.visibilityState === 'hidden'){
      this._handleUnloadEvent()
    }

    if (document.visibilityState === 'visible'){
      this._resetState()
    }
  }

  _handleUnloadEvent = () => {
    const { unloadProcessed } = this.state
    const { collectionKey, recordEvent, trackingMeta } = this.props

    if (!unloadProcessed){
      const analyticsData = this._getAnalyticsData()
      const mergedData = {
        ...trackingMeta,
        ...analyticsData,
      }

      recordEvent(collectionKey, mergedData, { useBeaconAPI: true })
    }
  }

  render(){
    const { render } = this.props
    return render()
  }
}

AnalyticsTracker.propTypes = {
  clientConfig: PropTypes.object.isRequired,
  collectionKey: PropTypes.string.isRequired,
  render: PropTypes.func.isRequired,
  initializeClient: PropTypes.func.isRequired,
  recordEvent: PropTypes.func.isRequired,
  trackingMeta: PropTypes.object,
  trackScrollPosition: PropTypes.bool,
}

function mapStateToProps(){
  return {}
}

const mapDispatchToProps = {
  initializeClient: eventTrackerActions.initializeClient,
  recordEvent: eventTrackerActions.recordEvent,
}

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(AnalyticsTracker)
